import React, { useEffect, useState } from "react";
import { ShieldCheck, ExternalLink, FileWarning } from "lucide-react";
import { getPlaceFacts } from "@/lib/api";
import { T } from "@/context/LanguageContext";

const LEVEL = {
  official: { dot: "bg-emerald-500", label: "Official" },
  verified: { dot: "bg-blue-500", label: "Verified" },
  community: { dot: "bg-amber-500", label: "Community" },
  unverified: { dot: "bg-slate-400", label: "Unverified" },
};

function scrollToContribute() {
  const el = document.querySelector("[data-testid='community-contributions']");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * "Data & Sources" trust panel — lists every governed fact with its source,
 * reference year, verification level and when it was last checked, plus a
 * "Correct this page" shortcut. Satisfies the City Page System trust footer.
 */
export function TrustPanel({ geoId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    getPlaceFacts(geoId).then((d) => { if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [geoId]);

  if (!data || !data.facts?.length) return null;
  const checked = data.updated_at ? new Date(data.updated_at).toLocaleDateString() : null;
  const verifiedThrough = data.updated_at
    ? new Date(data.updated_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : null;

  return (
    <section className="mx-auto max-w-[1500px] px-4 py-10" data-testid="trust-panel">
      <div className="rounded-2xl border-2 border-navy/15 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy text-white">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-display text-xl font-black text-navy"><T>Data & Sources</T></h3>
                {verifiedThrough && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-500/20" data-testid="verified-through">
                    <ShieldCheck className="h-3 w-3" /> <T>Verified through</T> {verifiedThrough}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                <T>Every fact is traceable to an authoritative source.</T>
                {checked && <> · <T>Sources last checked</T> {checked}</>}
              </p>
            </div>
          </div>
          <button
            onClick={scrollToContribute}
            className="inline-flex items-center gap-1.5 rounded-full border border-oldglory-red/30 px-4 py-2 text-sm font-bold text-oldglory-red transition-colors hover:bg-red-50"
            data-testid="trust-correct-btn"
          >
            <FileWarning className="h-4 w-4" /> <T>Correct this page</T>
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-border" data-testid="trust-table">
          <table className="w-full text-sm">
            <thead className="bg-parchment/60 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-bold"><T>Fact</T></th>
                <th className="px-3 py-2 text-left font-bold"><T>Value</T></th>
                <th className="hidden px-3 py-2 text-left font-bold sm:table-cell"><T>Source</T></th>
                <th className="px-3 py-2 text-left font-bold"><T>As of</T></th>
                <th className="px-3 py-2 text-left font-bold"><T>Trust</T></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.facts.map((f) => {
                const lvl = LEVEL[f.verification] || LEVEL.verified;
                return (
                  <tr key={f.key} data-testid={`trust-row-${f.key}`}>
                    <td className="px-3 py-2 font-semibold text-navy"><T>{f.label}</T></td>
                    <td className="px-3 py-2 text-ink">{f.value}</td>
                    <td className="hidden px-3 py-2 sm:table-cell">
                      {f.source?.url ? (
                        <a href={f.source.url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-xs font-medium text-oldglory-red hover:underline">
                          {f.source.label} <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : <span className="text-xs text-muted-foreground">{f.source?.label || "—"}</span>}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">{f.year || "—"}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy">
                        <span className={`h-1.5 w-1.5 rounded-full ${lvl.dot}`} /> <T>{lvl.label}</T>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default TrustPanel;
